import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  AlertTriangle,
  RefreshCw,
  Search,
  X,
  CheckCircle,
  TrendingUp,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, fetchImportPdf, getReportNotes } from '../utils/api';
import { detectFileViewerType, getFileNameFromPath } from '../utils/fileViewer';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { useTheme } from '../context/ThemeContext';
import API_ENDPOINTS from '../config/apiConfig';
import AlertNotesModal from '../components/NightAuditAlerts/AlertNotesModal';
import FileViewerModal from '../components/FileViewerModal';
import {
  AlertRow,
  CATEGORY_CONFIG,
  Badge,
  ResolveAllButton,
  SkeletonList,
} from '../components/NightAuditAlerts/NightAuditAlerts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };

const SORT_OPTIONS = [
  { key: 'severity',   label: 'Severity' },
  { key: 'hotel',      label: 'Hotel' },
  { key: 'date',       label: 'Date' },
  { key: 'recurrence', label: 'Recurrence' },
];

// Reusable card surface styles — same visual as <Card> but we render it ourselves
// so we can make it a flex column and give the list its own overflow.
const CARD_SURFACE = {
  borderRadius: 'var(--radius-xl)',
  background: 'var(--lodge-white)',
  border: '1px solid var(--lodge-onyx-100)',
  boxShadow: 'var(--shadow-sm)',
};

// ---------------------------------------------------------------------------
// NightAuditPage
// ---------------------------------------------------------------------------
export default function NightAuditPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization, userSettings } = useUserSettingsContext();
  const { insetActive } = useTheme();
  const orgId = currentOrganization?.id;
  const orgCode = currentOrganization?.code;
  const locationIds = userSettings?.orgAccess?.[orgCode]?.locationIds || [];

  const [flags, setFlags]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [resolvingAll, setResolvingAll] = useState(false);
  const [analyzedAt, setAnalyzedAt]     = useState(null);

  // View mode: open (active) or resolved
  const [viewMode, setViewMode] = useState('open');

  // Filters & sort
  const [searchQuery, setSQ]       = useState('');
  const [severityFilter, setSev]   = useState('all');
  const [categoryFilter, setCat]   = useState('all');
  const [hotelFilter, setHotel]    = useState('all');
  const [sortBy, setSortBy]        = useState('severity');

  // Notes & file viewer
  const [noteModalFlag, setNoteModalFlag] = useState(null);
  const [fileViewer, setFileViewer]               = useState(null);
  const [noteCounts, setNoteCounts]               = useState(new Map());
  const [preloadedNotes, setPreloadedNotes]       = useState(new Map());

  // Track which flagId we've already processed for deep-link
  const processedFlagId = useRef(null);

  const isResolved = viewMode === 'resolved';

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const fetchAllNotes = useCallback(async (flagList) => {
    if (!orgId || !flagList.length) return;
    const counts = new Map();
    const cache = new Map();
    await Promise.all(flagList.map(async (flag) => {
      try {
        const res = await getReportNotes({ orgId, reportId: 'night-audit-alerts', pageId: String(flag.id) });
        const notes = (res.notes || []).filter(n => !n.isDeleted);
        if (notes.length > 0) {
          counts.set(flag.id, notes.length);
          cache.set(flag.id, notes);
        }
      } catch { /* non-fatal */ }
    }));
    setNoteCounts(counts);
    setPreloadedNotes(cache);
  }, [orgId]);

  const fetchAlerts = useCallback(async (isRefresh = false, mode = viewMode) => {
    if (!orgId) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const locParam = locationIds.length ? `&locationIds=${locationIds.join(',')}` : '';
      const resolvedParam = mode === 'resolved' ? '&resolved=true' : '';
      const res = await api.get(`${API_ENDPOINTS.NIGHT_AUDIT_ALERTS}?orgId=${orgId}${locParam}${resolvedParam}`);
      const data = res.data;
      const fetched = data.flags || [];
      setFlags(fetched);
      const dates = fetched.map(f => f.analyzedAt).filter(Boolean);
      if (dates.length) setAnalyzedAt(new Date(Math.max(...dates.map(d => new Date(d)))));
      fetchAllNotes(fetched);
      return fetched;
    } catch (err) {
      console.error('Failed to fetch night audit alerts', err);
      return [];
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orgId, locationIds, fetchAllNotes, viewMode]);

  // Re-fetch when viewMode changes
  useEffect(() => {
    clearFilters();
    fetchAlerts(false, viewMode);
  }, [viewMode, orgId]);

  // ---------------------------------------------------------------------------
  // Deep-link handling: ?flagId=123&openNotes=true
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (loading) return;

    const flagId = searchParams.get('flagId');
    const openNotes = searchParams.get('openNotes') === 'true';
    if (!flagId) return;

    const numFlagId = parseInt(flagId, 10);
    const found = flags.find(f => f.id === numFlagId);

    if (found) {
      // Flag found — open modal if requested, clean up
      processedFlagId.current = null;
      if (openNotes) setNoteModalFlag(found);
      setSearchParams({}, { replace: true });
    } else if (viewMode === 'open' && processedFlagId.current !== numFlagId) {
      // Not in open view — switch to resolved and try again after re-fetch
      processedFlagId.current = numFlagId;
      setViewMode('resolved');
    } else {
      // Already tried resolved (or open had no match) — give up
      processedFlagId.current = null;
      setSearchParams({}, { replace: true });
    }
  }, [flags, loading, searchParams, viewMode, setSearchParams]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const removeFlag = (id) => setFlags(prev => prev.filter(f => f.id !== id));

  const resolve = async (id, resolutionNote) => {
    removeFlag(id);
    try {
      await api.patch(API_ENDPOINTS.NIGHT_AUDIT_ALERTS, { action: 'resolve', id, resolutionNote: resolutionNote || undefined });
    } catch { fetchAlerts(); }
  };

  const ignore = async (id) => {
    removeFlag(id);
    try {
      await api.patch(API_ENDPOINTS.NIGHT_AUDIT_ALERTS, { action: 'ignore', id });
    } catch { fetchAlerts(); }
  };

  const resolveAll = async () => {
    if (!orgId) return;
    setResolvingAll(true);
    try {
      await api.patch(API_ENDPOINTS.NIGHT_AUDIT_ALERTS, { action: 'resolveAll', orgId });
      setFlags([]);
    } catch (err) {
      console.error('Failed to resolve all', err);
    } finally {
      setResolvingAll(false);
    }
  };

  const openFile = useCallback(async (blobPath, sourcePage) => {
    const fileType = detectFileViewerType(blobPath, 'text');
    const url = await fetchImportPdf(blobPath);
    setFileViewer({ url, fileType, fileName: getFileNameFromPath(blobPath), initialPage: sourcePage || null });
  }, []);

  // ---------------------------------------------------------------------------
  // Derived stats
  // ---------------------------------------------------------------------------
  const stats = useMemo(() => ({
    total:     flags.length,
    high:      flags.filter(f => f.severity === 'high').length,
    medium:    flags.filter(f => f.severity === 'medium').length,
    low:       flags.filter(f => f.severity === 'low').length,
    recurring: flags.filter(f => f.recurrenceCount > 1).length,
    escalated: flags.filter(f => f.wasEscalated).length,
  }), [flags]);

  const categoryCounts = useMemo(() => {
    const c = {};
    flags.forEach(f => { c[f.category] = (c[f.category] || 0) + 1; });
    return c;
  }, [flags]);

  const hotelOptions = useMemo(() => {
    const hotels = [...new Map(flags.map(f => [f.hotelName, f.hotelName])).keys()]
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ key: name, label: name }));
    return [{ key: 'all', label: 'All Hotels' }, ...hotels];
  }, [flags]);

  // ---------------------------------------------------------------------------
  // Filtering + sorting
  // ---------------------------------------------------------------------------
  const filteredFlags = useMemo(() => {
    let result = flags;
    if (severityFilter !== 'all') result = result.filter(f => f.severity === severityFilter);
    if (categoryFilter !== 'all') result = result.filter(f => f.category === categoryFilter);
    if (hotelFilter    !== 'all') result = result.filter(f => f.hotelName === hotelFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(f =>
        f.title?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        f.hotelName?.toLowerCase().includes(q) ||
        f.recommendedAction?.toLowerCase().includes(q)
      );
    }
    return [...result].sort((a, b) => {
      if (sortBy === 'severity') {
        const sd = (b.severityScore ?? 0) - (a.severityScore ?? 0);
        if (sd !== 0) return sd;
        const sv = (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3);
        if (sv !== 0) return sv;
        return (a.hotelName || '').localeCompare(b.hotelName || '');
      }
      if (sortBy === 'hotel') {
        const hd = (a.hotelName || '').localeCompare(b.hotelName || '');
        if (hd !== 0) return hd;
        return (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3);
      }
      if (sortBy === 'date')       return new Date(b.lastSeenAt) - new Date(a.lastSeenAt);
      if (sortBy === 'recurrence') return b.recurrenceCount - a.recurrenceCount;
      return 0;
    });
  }, [flags, severityFilter, categoryFilter, hotelFilter, searchQuery, sortBy]);

  const isFiltered = severityFilter !== 'all' || categoryFilter !== 'all' || hotelFilter !== 'all' || searchQuery.trim();
  const clearFilters = () => { setSQ(''); setSev('all'); setCat('all'); setHotel('all'); };

  // ---------------------------------------------------------------------------
  // Render — outer div fills parent height so list can scroll internally
  // ---------------------------------------------------------------------------
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      // Inset shell: full-bleed route filling the card below its header.
      // Classic shell: the original viewport offset.
      height: insetActive
        ? 'calc(100svh - var(--shell-sticky-top, 96px) - var(--shell-gap, 0px) - 2px)'
        : 'calc(100vh - 96px)',
      maxWidth: 1100,
      margin: '0 auto',
      padding: insetActive ? '24px 16px 16px' : '24px 0 16px',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>

      {/* ── Top section (never scrolls) ── */}
      <div style={{ flexShrink: 0 }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', fontSize: 13, fontFamily: 'var(--font-sans)',
                color: 'var(--lodge-onyx-500)', background: 'transparent',
                border: '1px solid var(--lodge-onyx-200)', borderRadius: 'var(--radius-md)', cursor: 'pointer',
              }}
            >
              <ArrowLeft size={14} /> Dashboard
            </button>
            <div>
              <h1 style={{
                margin: 0, fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-sans)',
                color: 'var(--lodge-onyx-900)', display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <AlertTriangle size={20} color="#F97316" />
                Night Audit Alerts
                {!isResolved && stats.high > 0 && <Badge color="#B42318" bg="var(--color-error-bg)">{stats.high} High</Badge>}
              </h1>
              {analyzedAt && (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)' }}>
                  Last analyzed {analyzedAt.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => fetchAlerts(true)}
              disabled={refreshing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', fontSize: 13, fontFamily: 'var(--font-sans)',
                color: 'var(--lodge-onyx-600)', background: 'transparent',
                border: '1px solid var(--lodge-onyx-200)', borderRadius: 'var(--radius-md)',
                cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1,
              }}
            >
              <RefreshCw size={13} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            {!isResolved && flags.length > 0 && <ResolveAllButton onClick={resolveAll} loading={resolvingAll} />}
          </div>
        </div>

        {/* Open / Resolved tab toggle */}
        <div style={{ display: 'flex', gap: 2, background: 'var(--lodge-onyx-100)', borderRadius: 'var(--radius-md)', padding: 2, marginBottom: 16, width: 'fit-content' }}>
          {[
            { key: 'open', label: 'Open' },
            { key: 'resolved', label: 'Resolved' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setViewMode(tab.key)}
              style={{
                padding: '6px 16px', fontSize: 13, fontFamily: 'var(--font-sans)',
                fontWeight: viewMode === tab.key ? 600 : 400,
                color: viewMode === tab.key ? 'var(--lodge-onyx-800)' : 'var(--lodge-onyx-500)',
                background: viewMode === tab.key ? 'var(--lodge-white)' : 'transparent',
                border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                boxShadow: viewMode === tab.key ? 'var(--shadow-xs)' : 'none',
                transition: 'all var(--duration-fast)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Summary stats */}
        {!loading && !isResolved && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 16 }}>
            <StatCard label="Total Open"    value={stats.total} />
            <StatCard label="High Priority" value={stats.high}      color="#B42318"                bg="var(--color-error-bg)" />
            <StatCard label="Medium"        value={stats.medium}    color="#92400E"                bg="var(--color-warning-bg)" />
            <StatCard label="Low"           value={stats.low}       color="var(--lodge-onyx-500)" bg="var(--lodge-onyx-50)" />
            <StatCard label="Recurring"     value={stats.recurring} color="var(--lodge-onyx-700)" bg="var(--lodge-onyx-100)" />
            {stats.escalated > 0 && (
              <StatCard label="Escalated" value={stats.escalated} color="#B42318" bg="var(--color-error-bg)" icon={<TrendingUp size={13} />} />
            )}
          </div>
        )}

        {/* Resolved stats */}
        {!loading && isResolved && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 16 }}>
            <StatCard label="Total Resolved" value={stats.total} color="#027A48" bg="var(--color-success-bg, #ECFDF3)" />
            <StatCard label="High" value={stats.high} color="#B42318" bg="var(--color-error-bg)" />
            <StatCard label="Medium" value={stats.medium} color="#92400E" bg="var(--color-warning-bg)" />
            <StatCard label="Low" value={stats.low} color="var(--lodge-onyx-500)" bg="var(--lodge-onyx-50)" />
          </div>
        )}

        {/* Category chips */}
        {!loading && flags.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12,
            padding: '10px 14px', ...CARD_SURFACE,
          }}>
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
              const count = categoryCounts[key] || 0;
              if (!count) return null;
              const active = categoryFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setCat(active ? 'all' : key)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '3px 10px', fontSize: 12, fontFamily: 'var(--font-sans)',
                    fontWeight: active ? 700 : 500,
                    background: active ? cfg.color : cfg.bg,
                    color: active ? '#fff' : cfg.color,
                    border: `1px solid ${cfg.color}`, borderRadius: 'var(--radius-full)',
                    cursor: 'pointer', transition: 'all var(--duration-fast)',
                    opacity: active || categoryFilter === 'all' ? 1 : 0.55,
                  }}
                >
                  {cfg.label}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 16, height: 16, borderRadius: '50%', fontSize: 10, fontWeight: 700,
                    background: active ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)',
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
            {categoryFilter !== 'all' && (
              <button
                onClick={() => setCat('all')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', fontSize: 12, fontFamily: 'var(--font-sans)',
                  color: 'var(--lodge-onyx-500)', background: 'transparent',
                  border: '1px solid var(--lodge-onyx-200)', borderRadius: 'var(--radius-full)', cursor: 'pointer',
                }}
              >
                <X size={10} /> Clear
              </button>
            )}
          </div>
        )}

        {/* Filter + sort bar */}
        {!loading && flags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 180, maxWidth: 320 }}>
              <Search size={13} style={{
                position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--lodge-onyx-400)', pointerEvents: 'none',
              }} />
              <input
                type="text"
                placeholder="Search alerts…"
                value={searchQuery}
                onChange={e => setSQ(e.target.value)}
                style={{
                  width: '100%', paddingLeft: 28, paddingRight: searchQuery ? 28 : 10,
                  paddingTop: 6, paddingBottom: 6, fontSize: 13, fontFamily: 'var(--font-sans)',
                  border: '1px solid var(--lodge-onyx-200)', borderRadius: 'var(--radius-md)',
                  background: 'var(--lodge-white)', color: 'var(--lodge-onyx-800)',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSQ('')}
                  style={{
                    position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--lodge-onyx-400)', padding: 2, display: 'flex', alignItems: 'center',
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Severity pills */}
            <SeverityPills value={severityFilter} onChange={setSev} counts={stats} />

            {/* Hotel filter */}
            {hotelOptions.length > 2 && (
              <select
                value={hotelFilter}
                onChange={e => setHotel(e.target.value)}
                style={{
                  padding: '5px 10px', fontSize: 12, fontFamily: 'var(--font-sans)',
                  border: '1px solid var(--lodge-onyx-200)', borderRadius: 'var(--radius-md)',
                  background: 'var(--lodge-white)',
                  color: hotelFilter !== 'all' ? 'var(--lodge-onyx-900)' : 'var(--lodge-onyx-500)',
                  cursor: 'pointer', outline: 'none',
                }}
              >
                {hotelOptions.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            )}

            {/* Sort */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
              <span style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>Sort:</span>
              <div style={{ display: 'flex', gap: 2, background: 'var(--lodge-onyx-100)', borderRadius: 'var(--radius-md)', padding: 2 }}>
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    style={{
                      padding: '3px 9px', fontSize: 12, fontFamily: 'var(--font-sans)',
                      fontWeight: sortBy === opt.key ? 600 : 400,
                      color: sortBy === opt.key ? 'var(--lodge-onyx-800)' : 'var(--lodge-onyx-500)',
                      background: sortBy === opt.key ? 'var(--lodge-white)' : 'transparent',
                      border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      boxShadow: sortBy === opt.key ? 'var(--shadow-xs)' : 'none',
                      transition: 'all var(--duration-fast)', whiteSpace: 'nowrap',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results count + clear filters */}
        {!loading && isFiltered && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)' }}>
              {filteredFlags.length} of {flags.length} alert{flags.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={clearFilters}
              style={{
                fontSize: 12, fontFamily: 'var(--font-sans)', fontWeight: 500,
                color: 'var(--lodge-orange-400, #FF7A00)', background: 'none', border: 'none', cursor: 'pointer',
              }}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
      {/* end top section */}

      {/* ── Scrollable card — flex: 1 fills remaining height ── */}
      {loading ? (
        <div style={{ ...CARD_SURFACE, padding: 'var(--sp-6)' }}>
          <SkeletonList />
        </div>
      ) : flags.length === 0 ? (
        isResolved ? (
          <div style={{ ...CARD_SURFACE, flex: 1 }}>
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--lodge-onyx-500)', fontFamily: 'var(--font-sans)' }}>
                No resolved alerts yet.
              </p>
            </div>
          </div>
        ) : (
          <AllClearState />
        )
      ) : filteredFlags.length === 0 ? (
        <div style={{ ...CARD_SURFACE, padding: 'var(--sp-6)' }}>
          <p style={{ textAlign: 'center', padding: '32px 0', fontSize: 14, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)' }}>
            No alerts match the current filters.
          </p>
        </div>
      ) : (
        <div style={{
          ...CARD_SURFACE,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Scrollable list */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 var(--sp-6)' }}>
            {filteredFlags.map((flag, idx) => (
              <AlertRow
                key={flag.id}
                flag={flag}
                isLast={idx === filteredFlags.length - 1}
                onResolve={(note) => resolve(flag.id, note)}
                onIgnore={() => ignore(flag.id)}
                onOpenNotes={() => setNoteModalFlag(flag)}
                onOpenFile={openFile}
                noteCount={noteCounts.get(flag.id) || 0}
                resolvedInfo={isResolved ? {
                  resolvedBy: flag.resolvedBy,
                  resolvedAt: flag.resolvedAt,
                  resolutionNote: flag.resolutionNote,
                } : undefined}
              />
            ))}
          </div>

          {/* Footer — always visible at bottom of card */}
          <div style={{
            flexShrink: 0,
            padding: '10px var(--sp-6)',
            borderTop: '1px solid var(--lodge-onyx-100)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)' }}>
              {filteredFlags.length} {isResolved ? 'resolved' : ''} alert{filteredFlags.length !== 1 ? 's' : ''}
              {isFiltered ? ` (filtered from ${flags.length})` : ''}
              {!isResolved && analyzedAt ? ` · Analyzed ${analyzedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
            </span>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {noteModalFlag && (
        <AlertNotesModal
          flag={noteModalFlag}
          orgId={orgId}
          initialNotes={preloadedNotes.get(noteModalFlag.id)}
          onClose={() => {
            setNoteModalFlag(null);
            fetchAllNotes(flags);
          }}
          onNoteCountChange={() => fetchAllNotes(flags)}
        />
      )}

      {/* File Viewer */}
      {fileViewer && (
        <FileViewerModal
          url={fileViewer.url}
          fileType={fileViewer.fileType}
          fileName={fileViewer.fileName}
          initialPage={fileViewer.initialPage}
          onClose={() => setFileViewer(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------
function StatCard({ label, value, color, bg, icon }) {
  return (
    <div style={{
      padding: 'var(--sp-4) var(--sp-4)', background: bg || 'var(--lodge-white)',
      border: '1px solid var(--lodge-onyx-100)', borderRadius: 'var(--radius-xl)',
      boxShadow: 'var(--shadow-sm)',
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
    }}>
      <span style={{
        fontSize: 12, fontFamily: 'var(--font-sans)', color: 'var(--lodge-onyx-400)',
        fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {icon}{label}
      </span>
      <span style={{ fontSize: 32, fontFamily: 'var(--font-display)', color: color || 'var(--lodge-onyx-900)', lineHeight: 1, letterSpacing: '-1px' }}>
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SeverityPills
// ---------------------------------------------------------------------------
function SeverityPills({ value, onChange, counts }) {
  const options = [
    { key: 'all',    label: 'All' },
    { key: 'high',   label: 'High',   count: counts.high,   color: '#B42318',                  bg: 'var(--color-error-bg)' },
    { key: 'medium', label: 'Medium', count: counts.medium, color: '#92400E',                  bg: 'var(--color-warning-bg)' },
    { key: 'low',    label: 'Low',    count: counts.low,    color: 'var(--lodge-onyx-500)',    bg: 'var(--lodge-onyx-100)' },
  ];
  return (
    <div style={{ display: 'flex', gap: 2, background: 'var(--lodge-onyx-100)', borderRadius: 'var(--radius-md)', padding: 2 }}>
      {options.map(opt => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', fontSize: 12, fontFamily: 'var(--font-sans)',
            fontWeight: value === opt.key ? 600 : 400,
            color: value === opt.key && opt.color ? opt.color : value === opt.key ? 'var(--lodge-onyx-800)' : 'var(--lodge-onyx-500)',
            background: value === opt.key ? (opt.bg || 'var(--lodge-white)') : 'transparent',
            border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
            boxShadow: value === opt.key ? 'var(--shadow-xs)' : 'none',
            transition: 'all var(--duration-fast)', whiteSpace: 'nowrap',
          }}
        >
          {opt.label}
          {opt.count != null && opt.count > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, borderRadius: '50%', padding: '0 4px',
              minWidth: 16, textAlign: 'center',
              background: value === opt.key && opt.color ? opt.color : 'var(--lodge-onyx-200)',
              color: value === opt.key && opt.color ? '#fff' : 'var(--lodge-onyx-600)',
            }}>
              {opt.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AllClearState
// ---------------------------------------------------------------------------
function AllClearState() {
  return (
    <div style={{ ...CARD_SURFACE, flex: 1 }}>
      <div style={{ textAlign: 'center', padding: '48px 24px' }}>
        <CheckCircle size={48} color="var(--color-success, #12B76A)" style={{ marginBottom: 16 }} />
        <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, fontFamily: 'var(--font-sans)', color: 'var(--lodge-onyx-800)' }}>
          All Clear
        </h3>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--lodge-onyx-500)', fontFamily: 'var(--font-sans)' }}>
          No active night audit alerts across all hotels.
        </p>
      </div>
    </div>
  );
}
